import { useState } from 'react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { Combobox } from 'src/shared/components/Combobox'
import { Column } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { useDebounce } from 'src/shared/hooks/useDebounce'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'

export const ChangeOwnershipDialog = ({
  open,
  onOpenChange,
  publicId,
  currentOrgName,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  publicId: string
  currentOrgName?: string | null
  onSuccess?: () => void
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { updatePublicQuote } = useQuoteApi({ skipListQuery: true })
  const { useSearchOrganizations } = useOrgsApi({})

  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [orgSearchInput, setOrgSearchInput] = useState('')
  const debouncedOrgSearch = useDebounce(orgSearchInput, 300)

  const { data: searchedOrgs } = useSearchOrganizations({
    search: debouncedOrgSearch,
    limit: 20,
    excludeAnon: true,
    excludeEmptyName: true,
    enabled: open,
  })

  const orgOptions = (searchedOrgs?.data ?? []).map((org) => ({
    value: org.id,
    label: org.name,
  }))

  const ownerLabel =
    currentOrgName?.trim() || t('Unassigned', 'Otilldelad')

  const handleAssign = async () => {
    if (!selectedOrgId) return
    try {
      await updatePublicQuote.mutateAsync({
        public_id: publicId,
        org_id: selectedOrgId,
      })
      toast({
        variant: 'success',
        title: t('Ownership updated', 'Ägarskap uppdaterat'),
      })
      setSelectedOrgId('')
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Failed to change ownership:', error)
      toast({
        variant: 'destructive',
        title: t('Could not change ownership', 'Kunde inte byta ägarskap'),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('Change ownership', 'Byt ägarskap')}
          </DialogTitle>
        </DialogHeader>

        <Column className="gap-4">
          <p className="text-sm text-muted-foreground">
            {t('Current owner', 'Nuvarande ägare')}:{' '}
            <span className="text-foreground">{ownerLabel}</span>
          </p>
          <Combobox
            ariaLabel={t('Organization', 'Organisation')}
            value={selectedOrgId}
            onValueChange={setSelectedOrgId}
            options={orgOptions}
            onSearchChange={setOrgSearchInput}
            placeholder={t('Select organization', 'Välj organisation')}
            searchPlaceholder={t(
              'Search organization…',
              'Sök organisation…'
            )}
            emptyMessage={t(
              'No organizations found.',
              'Inga organisationer hittades.'
            )}
          />
        </Column>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={updatePublicQuote.isPending}
          >
            {t('Cancel', 'Avbryt')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleAssign()}
            disabled={!selectedOrgId || updatePublicQuote.isPending}
            isLoading={updatePublicQuote.isPending}
          >
            {t('Assign', 'Tilldela')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
